"use client";

import { useState } from "react";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  LineChart,
  type LineRow,
} from "@/components/tool-kit/charts/line-chart";
import {
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  COMPOUNDING_KEYS,
  CurrencyField,
  frequencyOptions,
  perYearOf,
  SCHEDULE_KEYS,
  useMoney,
  usePercent,
} from "@/components/tools/finance/shared";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberOr } from "@/lib/parse-number";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type CompoundInput,
  type CompoundResult,
  compoundSchedule,
} from "@/lib/tool-kit/finance/compound";
import { PLAIN } from "@/lib/tool-kit/finance/currency";
import {
  CONTINUOUS,
  effectiveAnnualRate,
  growthFactor,
  type Timing,
} from "@/lib/tool-kit/finance/tvm";

/** English strings. A translated content file overrides these through `ui`. */
export const COMPOUND_INTEREST_CALCULATOR_STRINGS = {
  inputsHeading: "Your numbers",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  principal: "Starting amount",
  rate: "Annual interest rate (%)",
  years: "Time (years)",
  compounding: "Interest is compounded",
  deposit: "Regular deposit",
  depositFrequency: "Deposits are made",
  depositTiming: "Each deposit is made at the",
  timingEnd: "End of each period",
  timingBegin: "Start of each period",
  annually: "Annually (1 per year)",
  semiannually: "Semiannually (2 per year)",
  quarterly: "Quarterly (4 per year)",
  bimonthly: "Every two months (6 per year)",
  monthly: "Monthly (12 per year)",
  semimonthly: "Twice a month (24 per year)",
  biweekly: "Every two weeks (26 per year)",
  weekly: "Weekly (52 per year)",
  daily: "Daily (365 per year)",
  continuous: "Continuously",
  resultsHeading: "Results",
  futureValue: "Future value",
  totalContributed: "Total you put in",
  interestEarned: "Interest earned",
  effectiveRate: "Effective annual rate (APY)",
  workingHeading: "The working",
  workDiscrete:
    "Starting amount: {principal} × (1 + {rate} ÷ {m})^({m} × {years}) = {grown}",
  workContinuous:
    "Starting amount: {principal} × e^({rate} × {years}) = {grown}",
  workDeposits:
    "{count} deposits of {deposit}, grown to the end of the term = {depositsGrown}",
  workTotal: "Future value: {grown} + {depositsGrown} = {total}",
  workApy: "Effective annual rate: (1 + {rate} ÷ {m})^{m} − 1 = {apy}%",
  workApyContinuous: "Effective annual rate: e^{rate} − 1 = {apy}%",
  betweenDatesNote:
    "Deposits fall between compounding dates here, so the balance is grown by the equivalent rate in between. A bank that credits interest only on its compounding dates would show slightly less.",
  tableHeading: "Year by year",
  yearColumn: "Year",
  depositsColumn: "Deposits",
  interestColumn: "Interest",
  contributedColumn: "Total put in",
  balanceColumn: "Balance",
  chartTitle: "Balance growth",
  chartXTitle: "Years",
  chartYTitle: "Balance ({currency})",
  chartYTitlePlain: "Balance",
  chartLabel: "Balance growth chart",
  chartDescription:
    "Line chart of the balance and the total put in, from year 0 to year {last}. The balance ends at {end} against {put} put in.",
  seriesBalance: "Balance",
  seriesContributed: "Total put in",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  invalidRate: "Enter an interest rate of 0 or more.",
  invalidYears: "Enter a time between 0 and 100 years.",
  nothingToGrow: "Enter a starting amount or a regular deposit above zero.",
  convention:
    "Interest rate is a nominal annual rate; the balance grows by (1 + rate ÷ m) each compounding period.",
} as const;

type Strings = typeof COMPOUND_INTEREST_CALCULATOR_STRINGS;

const MAX_YEARS = 100;
const MAX_DEPOSIT_COUNT = 6000;

interface Parsed {
  input: CompoundInput | null;
  error: string | null;
}

function parseForm(
  form: Record<
    "principal" | "rate" | "years" | "deposit" | "compounding" | "depositFreq",
    string
  > & { timing: Timing },
  strings: Strings
): Parsed {
  const rate = parseNumberInput(form.rate);
  const years = parseNumberInput(form.years);
  const principal = Math.max(0, parseNumberOr(form.principal, 0));
  const deposit = Math.max(0, parseNumberOr(form.deposit, 0));
  const depositsPerYear = Number(form.depositFreq);
  if (rate === null || rate < 0) {
    return { input: null, error: strings.invalidRate };
  }
  if (
    years === null ||
    years <= 0 ||
    years > MAX_YEARS ||
    (deposit > 0 && years * depositsPerYear > MAX_DEPOSIT_COUNT)
  ) {
    return { input: null, error: strings.invalidYears };
  }
  if (principal === 0 && deposit === 0) {
    return { input: null, error: strings.nothingToGrow };
  }
  return {
    input: {
      principal,
      annualRate: rate / 100,
      perYear: perYearOf(form.compounding),
      years,
      deposit,
      depositsPerYear,
      timing: form.timing,
    },
    error: null,
  };
}

function chartRows(
  input: CompoundInput,
  result: CompoundResult,
  fmt: (v: number, o?: Intl.NumberFormatOptions) => string
): LineRow[] {
  const label = (year: number) => fmt(year, { maximumFractionDigits: 2 });
  return [
    {
      label: label(0),
      x: 0,
      values: [input.principal, input.principal],
    },
    ...result.rows.map((row) => ({
      label: label(row.year),
      x: row.year,
      values: [row.balance, row.contributed],
    })),
  ];
}

function WorkingLines({
  input,
  result,
  strings,
}: {
  input: CompoundInput;
  result: CompoundResult;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const percent = usePercent();
  const num = (v: number) => fmt(v, { maximumFractionDigits: 6 });
  const continuous = input.perYear === CONTINUOUS;
  const grown =
    input.principal *
    growthFactor(input.annualRate, input.perYear, input.years);
  const depositsGrown = result.finalBalance - grown;
  const money = (v: number) =>
    fmt(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const values = {
    principal: money(input.principal),
    rate: num(input.annualRate),
    m: fmt(input.perYear),
    years: num(input.years),
    grown: money(grown),
    count: fmt(result.depositCount),
    deposit: money(input.deposit),
    depositsGrown: money(depositsGrown),
    total: money(result.finalBalance),
    apy: percent(effectiveAnnualRate(input.annualRate, input.perYear), 6),
  };
  return (
    <div className={`${CARD} space-y-1`}>
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(continuous ? strings.workContinuous : strings.workDiscrete, values)}
      </p>
      {input.deposit > 0 ? (
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(strings.workDeposits, values)}
        </p>
      ) : null}
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(strings.workTotal, values)}
      </p>
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(continuous ? strings.workApyContinuous : strings.workApy, values)}
      </p>
    </div>
  );
}

function Results({
  input,
  currency,
  strings,
}: {
  input: CompoundInput;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const percent = usePercent();
  const result = compoundSchedule(input);
  const rows = chartRows(input, result, fmt);
  const names = [strings.seriesBalance, strings.seriesContributed];
  const yTitle =
    currency === PLAIN
      ? strings.chartYTitlePlain
      : t(strings.chartYTitle, { currency });
  const style = {
    title: strings.chartTitle,
    xTitle: strings.chartXTitle,
    yTitle,
    color: "blue" as const,
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  };
  const between =
    input.deposit > 0 &&
    Number.isFinite(input.perYear) &&
    input.perYear % input.depositsPerYear !== 0;
  const last = rows.at(-1);
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.futureValue}
          value={money(result.finalBalance)}
        />
        <StatCard
          label={strings.totalContributed}
          value={money(input.principal + result.totalDeposits)}
        />
        <StatCard
          label={strings.interestEarned}
          value={money(result.totalInterest)}
        />
        <StatCard
          label={strings.effectiveRate}
          value={`${percent(effectiveAnnualRate(input.annualRate, input.perYear), 4)}%`}
        />
      </StatGrid>
      {between ? <Notice>{strings.betweenDatesNote}</Notice> : null}
      <WorkingLines input={input} result={result} strings={strings} />
      {rows.length >= 2 ? (
        <ChartFigure
          apa={false}
          data={toTsv([
            [strings.yearColumn, ...names],
            ...rows.map((row) => [row.label, ...row.values]),
          ])}
          figureNumber="1"
          filename={chartFilename(strings.chartTitle, "compound-interest")}
          labels={strings}
          note=""
          table={null}
          title={strings.chartTitle}
        >
          <LineChart
            description={t(strings.chartDescription, {
              last: last?.label ?? "",
              end: money(result.finalBalance),
              put: money(input.principal + result.totalDeposits),
            })}
            fmt={fmt}
            id="ci-chart"
            label={strings.chartLabel}
            markers={rows.length <= 12}
            names={names}
            numeric
            rows={rows}
            style={style}
            zero
          />
        </ChartFigure>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[
          strings.yearColumn,
          strings.depositsColumn,
          strings.interestColumn,
          strings.contributedColumn,
          strings.balanceColumn,
        ]}
        rows={result.rows.map((row) => [
          fmt(row.year, { maximumFractionDigits: 2 }),
          money(row.deposits),
          money(row.interest),
          money(row.contributed),
          money(row.balance),
        ])}
      />
    </section>
  );
}

export default function CompoundInterestCalculatorWidget() {
  const strings = useToolUi(COMPOUND_INTEREST_CALCULATOR_STRINGS);
  const [currency, setCurrency] = useState("USD");
  const [principal, setPrincipal] = useState("10000");
  const [rate, setRate] = useState("6");
  const [years, setYears] = useState("10");
  const [compounding, setCompounding] = useState("12");
  const [deposit, setDeposit] = useState("200");
  const [depositFreq, setDepositFreq] = useState("12");
  const [timing, setTiming] = useState<Timing>("end");

  const parsed = parseForm(
    { principal, rate, years, deposit, compounding, depositFreq, timing },
    strings
  );

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <CurrencyField
            id="ci-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
          <TextField
            id="ci-principal"
            label={strings.principal}
            numeric
            onChange={setPrincipal}
            value={principal}
          />
          <TextField
            id="ci-rate"
            label={strings.rate}
            numeric
            onChange={setRate}
            value={rate}
          />
          <TextField
            id="ci-years"
            label={strings.years}
            numeric
            onChange={setYears}
            value={years}
          />
          <SelectField
            id="ci-compounding"
            label={strings.compounding}
            onChange={setCompounding}
            options={frequencyOptions(strings, COMPOUNDING_KEYS, true)}
            value={compounding}
          />
          <TextField
            id="ci-deposit"
            label={strings.deposit}
            numeric
            onChange={setDeposit}
            value={deposit}
          />
          <SelectField
            id="ci-deposit-frequency"
            label={strings.depositFrequency}
            onChange={setDepositFreq}
            options={frequencyOptions(strings, SCHEDULE_KEYS)}
            value={depositFreq}
          />
        </div>
        <PillGroup
          label={strings.depositTiming}
          name="ci-timing"
          onChange={setTiming}
          options={[
            { value: "end", label: strings.timingEnd },
            { value: "begin", label: strings.timingBegin },
          ]}
          value={timing}
        />
        <p className={HINT}>{strings.convention}</p>
      </section>
      {parsed.input ? (
        <Results currency={currency} input={parsed.input} strings={strings} />
      ) : (
        <Notice>{parsed.error}</Notice>
      )}
    </div>
  );
}
